import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode, type RefObject } from "react";

/**
 * Scrolls the embedding page so the widget's next screen starts in view.
 * Provided only inside the `<booking-form>` widget; `undefined` elsewhere.
 */
export type ScrollWidgetToScreenTop = () => void;

const WidgetScrollContext = createContext<ScrollWidgetToScreenTop | undefined>(undefined);

export function useWidgetScroll(): ScrollWidgetToScreenTop | undefined {
  return useContext(WidgetScrollContext);
}

function getScrollBehaviour(): ScrollBehavior {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth";
}

interface WidgetScrollProviderProps {
  /** The widget's root element, whose position the page is scrolled against. */
  readonly rootRef: RefObject<HTMLElement | null>;
  /** The scrollable wrapper used while the widget is maximised. */
  readonly contentRef: RefObject<HTMLElement | null>;
  /** Whether the widget is currently maximised. */
  readonly isMaximised: boolean;
  readonly children: ReactNode;
}

/**
 * Keeps the widget's screens where the guest left them on the embedding page.
 *
 * The page position is read on the guest's first interaction with the widget —
 * before any screen renders and before the browser makes an adjustment of its
 * own — and reused for every screen change afterwards, so the journey stays
 * where the guest found it rather than jumping. It is stored as an offset from
 * the widget's own top edge, so it stays correct when the page reflows or the
 * widget's height changes between screens.
 */
export function WidgetScrollProvider({
  rootRef,
  contentRef,
  isMaximised,
  children,
}: WidgetScrollProviderProps): React.ReactElement {
  const isMaximisedRef = useRef(isMaximised);
  isMaximisedRef.current = isMaximised;
  const offsetFromWidgetTopRef = useRef<number | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const captureOffset = (): void => {
      if (offsetFromWidgetTopRef.current !== null) return;
      // How far the page is scrolled past the widget's top edge, negative
      // while the widget starts below the top of the viewport.
      offsetFromWidgetTopRef.current = -root.getBoundingClientRect().top;
    };

    root.addEventListener("pointerdown", captureOffset, true);
    root.addEventListener("keydown", captureOffset, true);
    return () => {
      root.removeEventListener("pointerdown", captureOffset, true);
      root.removeEventListener("keydown", captureOffset, true);
    };
  }, [rootRef]);

  const scrollToScreenTop = useCallback<ScrollWidgetToScreenTop>(() => {
    const behavior = getScrollBehaviour();

    // Maximised, the page behind is scroll-locked and the journey scrolls
    // inside its own wrapper, so that wrapper is what needs resetting.
    if (isMaximisedRef.current) {
      contentRef.current?.scrollTo({ top: 0, behavior });
      return;
    }

    const root = rootRef.current;
    if (!root) return;

    const widgetTop = root.getBoundingClientRect().top + window.scrollY;
    if (offsetFromWidgetTopRef.current === null) {
      offsetFromWidgetTopRef.current = window.scrollY - widgetTop;
    }

    window.scrollTo({ top: Math.max(widgetTop + offsetFromWidgetTopRef.current, 0), behavior });
  }, [contentRef, rootRef]);

  return <WidgetScrollContext.Provider value={scrollToScreenTop}>{children}</WidgetScrollContext.Provider>;
}
